import { Box, List, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { matchPath, useLocation } from 'react-router-dom';
import { useSpace } from 'src/hooks/useSpace';
import { useWorkspaceUserFeatures } from 'src/hooks/useWorkspaceUserFeatures';

import workspaceMenuItems, {
  MenuItem,
  organisationItems
} from '../../menuItems';
import HeaderMenuItem from './item';

const MenuWrapper = styled(Box)(
  () => `

  .MuiList-root {
    padding: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    
    & > .MuiList-root {
      display: flex;
      flex-direction: row;
      width: 100%;
      flex-wrap: wrap;
    }

    & > .MuiListItem-root {
      white-space: nowrap;
    }
  }
`
);

const SubMenuWrapper = styled(Box)(
  ({ theme }) => `
    width: 100%;
    .MuiList-root {
      padding: 0;
      display: flex;
      flex-direction: row;

      .MuiList-root .MuiList-root .MuiListItem-root .MuiIconButton-root {
        font-weight: normal !important;
      }

      .MuiListItem-root {
        padding: 0 0px;

        justify-content: center;
        width: auto;

        &:last-child {
          margin-left: auto;
        }
    
        .MuiIconButton-root {
          display: flex;
          background-color: transparent;
          border-radius: ${theme.general.borderRadiusLg};
          justify-content: center;
          font-size: ${theme.typography.pxToRem(14)};
          padding: ${theme.spacing(1.4, 2)};
          position: relative;
          font-weight: bold;
          color: ${theme.colors.captur.primary};
          .name-wrapper {
            transition: ${theme.transitions.create(['color'])};
          }

          .MuiBadge-root {
            position: absolute;
            right: 16px;
            top: 12px;

            .MuiBadge-badge {
              background: ${theme.colors.alpha.white[70]};
              color: ${theme.colors.alpha.black[100]};
              font-size: ${theme.typography.pxToRem(11)};
              font-weight: bold;
              text-transform: uppercase;
            }
          }
  
          .MuiSvgIcon-root {
            transition: ${theme.transitions.create(['color'])};
            font-size: ${theme.typography.pxToRem(24)};
            margin-right: ${theme.spacing(1)};
            color: ${theme.colors.alpha.trueWhite[50]};
          }

          &.Mui-active,
          &:hover {
            background-color: ${theme.colors.alpha.white[10]};

            .MuiSvgIcon-root {
              color: ${theme.colors.alpha.trueWhite[100]};
            }
          }
        }
      }
    }
`
);

type shouldFeatureRender = ReturnType<
  typeof useWorkspaceUserFeatures
>['shouldFeatureRender'];

const renderHeaderMenuItems = (
  {
    items,
    path
  }: {
    items: MenuItem[];
    path: string;
  },
  shouldFeatureRender: shouldFeatureRender
): JSX.Element => {
  return (
    <SubMenuWrapper>
      <List component="div">
        {items.reduce(
          (ev, item) =>
            reduceChildRoutes({ ev, item, path, items }, shouldFeatureRender),
          []
        )}
      </List>
    </SubMenuWrapper>
  );
};

let itemIndex = 0; // Tracks index of the menu items, for rotating colors

export const reduceChildRoutes = (
  {
    ev,
    path,
    item,
    items
  }: {
    ev: JSX.Element[];
    path: string;
    item: MenuItem;
    items: MenuItem[];
  },
  shouldFeatureRender: shouldFeatureRender
): Array<JSX.Element> => {
  const key = item.name;

  const exactMatch = item.link
    ? !!matchPath(
        {
          path: item.link,
          end: true
        },
        path
      )
    : false;

  if (item.items) {
    const partialMatch = item.link
      ? !!matchPath(
          {
            path: item.link,
            end: false
          },
          path
        )
      : false;

    if (shouldFeatureRender(item.features)) {
      ev.push(
        <HeaderMenuItem
          key={key}
          active={partialMatch}
          open={partialMatch}
          name={item.name}
          icon={item.icon}
          link={item.link}
          features={item.features}
          badge={item.badge}
          badgeTooltip={item.badgeTooltip}
          description={item.description || null}
          index={itemIndex}
        >
          {renderHeaderMenuItems(
            {
              path,
              items: item.items
            },
            shouldFeatureRender
          )}
        </HeaderMenuItem>
      );
      itemIndex++;
    }
  } else {
    if (shouldFeatureRender(item.features)) {
      ev.push(
        <HeaderMenuItem
          key={key}
          active={exactMatch}
          name={item.name}
          icon={item.icon}
          link={item.link}
          features={item.features}
          badge={item.badge}
          badgeTooltip={item.badgeTooltip}
          description={item.description || null}
          index={itemIndex}
        />
      );
      itemIndex++;
    }
  }

  return ev;
};

function HeaderMenu() {
  const location = useLocation();
  const space = useSpace();
  const { t } = useTranslation();
  const { shouldFeatureRender } = useWorkspaceUserFeatures();

  const menuItems =
    space.spaceType === 'workspace' ? workspaceMenuItems : organisationItems;

  return (
    <>
      {menuItems.map(
        (section) =>
          shouldFeatureRender(section.features) && (
            <MenuWrapper key={section.heading}>
              <List component="div">
                {renderHeaderMenuItems(
                  {
                    items: section.items,
                    path: location.pathname
                  },
                  shouldFeatureRender
                )}
              </List>
            </MenuWrapper>
          )
      )}
    </>
  );
}

export default HeaderMenu;
